import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export default function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | success | error | missing
  const [message, setMessage] = useState("A confirmar o seu email...");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("missing");
      setMessage("Link de confirmação em falta ou incompleto.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then((res) => {
        setStatus("success");
        setMessage(res.data?.message || "Email confirmado com sucesso! Já pode entrar.");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Não foi possível confirmar o email. O link pode ter expirado.");
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8 paper-texture" data-testid="confirm-email-page">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex items-center gap-2.5 mb-10 text-primary">
          <BookOpen className="h-9 w-9" />
          <span className="font-heading font-bold text-3xl tracking-tight">EduGrade</span>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-xl">Confirmação de email</CardTitle>
            <CardDescription>Estamos a validar o seu endereço de email.</CardDescription>
          </CardHeader>
          <CardContent>
            {status === "loading" && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="confirm-loading">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{message}</span>
              </div>
            )}
            {status === "success" && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="confirm-success">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <span>{message}</span>
                </div>
                <Button className="w-full" onClick={() => navigate("/auth")} data-testid="confirm-login-btn">
                  Entrar
                </Button>
              </div>
            )}
            {(status === "error" || status === "missing") && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="confirm-error">
                  <XCircle className="h-5 w-5 shrink-0" />
                  <span>{message}</span>
                </div>
                <Button variant="outline" className="w-full" onClick={() => navigate("/auth")} data-testid="confirm-back-btn">
                  Voltar à página de entrada
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center mt-6">
          <Link to="/auth" className="text-primary hover:underline" data-testid="confirm-auth-link">Página de entrada</Link>
        </p>
      </div>
    </div>
  );
}
